/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { memo, useState } from 'react';

import { Button } from '@coursera/cds-core';

import CourseraPlusSearchFilter from 'bundles/search-common/components/filters/CourseraPlusSearchFilter';
import { partitionMicroLessonsItems } from 'bundles/search-common/components/filters/MicroLessonsSearchFilter';
import SearchFilterItem from 'bundles/search-common/components/filters/SearchFilterItem';
import type { EventingData } from 'bundles/search-common/components/filters/SearchFilterItem';
import {
  IS_PART_OF_COURSERA_PLUS,
  MICRO_LESSONS_FACET_KEY,
  NUMBER_OF_FILTER_ITEMS_TO_SHOW,
} from 'bundles/search-common/constants/filter';
import useIsMicroLessonsRolloutEnabled from 'bundles/search-common/hooks/useIsMicroLessonsRolloutEnabled';
import type { SearchFilterOption } from 'bundles/search-common/types';

import _t from 'i18n!nls/search-common';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
};

type Props = {
  attribute: string;
  filterName: string;
  showCPlusSupportText?: boolean;
  items?: SearchFilterOption[];
  eventingData: EventingData;
  addFilters: (filters: string) => void;
  removeFilters: (filters: string) => void;
};

const SearchFilterMobile = ({
  items = [],
  filterName,
  attribute,
  addFilters,
  removeFilters,
  eventingData,
  showCPlusSupportText,
}: Props) => {
  const [numOfItemsToShow, setNumOfItemToShow] = useState(NUMBER_OF_FILTER_ITEMS_TO_SHOW);
  const isMicroLessonsRolloutEnabled = useIsMicroLessonsRolloutEnabled();
  if (items.length === 0) {
    return null;
  }

  if (attribute === IS_PART_OF_COURSERA_PLUS) {
    return (
      <CourseraPlusSearchFilter
        name={filterName}
        items={items}
        addFilters={addFilters}
        removeFilters={removeFilters}
        eventingData={eventingData}
        showCPlusSupportText={showCPlusSupportText}
      />
    );
  }

  // SEARCH-2412: the two underlying short-form values are represented by the standalone "Micro
  // Lessons" toggle the surrounding surface renders above these groups, so drop them from the
  // Learning Product list rather than showing them twice. The surface hides this group outright
  // once the toggle is on (see useMicroLessonsHiddenFacetKeys).
  const displayItems =
    isMicroLessonsRolloutEnabled && attribute === MICRO_LESSONS_FACET_KEY
      ? partitionMicroLessonsItems(items).remainingItems
      : items;

  let itemsToShow = displayItems;
  if (displayItems.length > NUMBER_OF_FILTER_ITEMS_TO_SHOW) {
    itemsToShow = displayItems.slice(0, numOfItemsToShow);
  }

  return (
    <div css={styles.container}>
      {itemsToShow.map((item) => (
        <SearchFilterItem
          key={item.label}
          {...item}
          value={`${attribute}:${item.value}`}
          filterCategoryName={filterName}
          addFilters={addFilters}
          removeFilters={removeFilters}
          eventingData={eventingData}
          showSupportText={true}
        />
      ))}
      {numOfItemsToShow < displayItems.length && (
        <Button variant="ghost" onClick={() => setNumOfItemToShow(numOfItemsToShow + NUMBER_OF_FILTER_ITEMS_TO_SHOW)}>
          {_t('Show More')}
        </Button>
      )}
    </div>
  );
};

export default memo(SearchFilterMobile);
